import React, { useCallback, type KeyboardEvent, type MouseEvent } from 'react';
import { DropdownItem, type DropdownItemProps } from '@atlaskit/dropdown-menu';
import { useAnalyticsEvents, fireUIAnalytics } from '@atlassian/jira-product-analytics-bridge';
import type { ConnectPluggableEntity } from '../../../services/query/index.tsx';
import { buildStyleClass, translateAppTypeToAnalyticsType } from '../../utils.tsx';
import { AppIcon } from '../app-icon/index.tsx';

export type ConnectDropdownItemProps = {
	item: ConnectPluggableEntity;
	totalCount: number;
};

export const ConnectDropdownItem = ({ item, totalCount }: ConnectDropdownItemProps) => (
	<DropdownItemWithAnalytics
		testId="issue-navigator-action-pluggable-items.ui.dropdown-item.connect-dropdown-item.drop-down-item"
		key={item.id}
		id={item.id ?? undefined}
		title={typeof item.tooltip === 'string' ? item.tooltip : undefined}
		elemBefore={<AppIcon iconUrl={item.iconUrl} />}
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
		className={(item.metadata.target && buildStyleClass(item.metadata.target)) || ''}
		analyticsAppCount={totalCount}
		analyticsAppName={item.title ?? ''}
		analyticsAppType={translateAppTypeToAnalyticsType(item.metadata.target?.type)}
	>
		{item.title}
	</DropdownItemWithAnalytics>
);

type DropdownItemWithAnalyticsProps = DropdownItemProps & {
	id?: string;
	testId: string;
	className: string;
	// Analytics-only props
	analyticsAppName: string;
	analyticsAppCount: number;
	analyticsAppType: string;
};

export function DropdownItemWithAnalytics({
	analyticsAppName,
	analyticsAppCount,
	analyticsAppType,
	testId,
	...props
}: DropdownItemWithAnalyticsProps) {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const fireDropdownClickUIAnalytics = useCallback(
		(event: KeyboardEvent<Element> | MouseEvent<Element>) => {
			if ('key' in event && event.key != null && event.key !== 'Enter' && event.key !== ' ') {
				return;
			}

			fireUIAnalytics(createAnalyticsEvent({}), 'dropdownItem clicked', 'apps', {
				uniqueKey: props.id,
				source: 'key' in event ? 'keypress' : 'click',
				appCount: analyticsAppCount,
				appName: analyticsAppName,
				appType: analyticsAppType,
			});
		},
		[analyticsAppCount, analyticsAppName, analyticsAppType, createAnalyticsEvent, props.id],
	);
	return <DropdownItem {...props} onClick={fireDropdownClickUIAnalytics} testId={testId} />;
}
